/**
 * Logging utilities.
 *
 * - logDev/warnDev/errorDev: dev-only console wrappers (stripped in prod build).
 * - reportClientError: POST a sanitized error report to /api/client-errors so
 *   admins can investigate without asking customers to copy logs.
 * - installGlobalErrorHandlers: wires window.onerror + unhandledrejection.
 */
import axios from "axios";

const isDev = process.env.NODE_ENV === "development";

export const logDev = (...args) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.log("[mama]", ...args);
  }
};

export const warnDev = (...args) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.warn("[mama]", ...args);
  }
};

export const errorDev = (...args) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.error("[mama]", ...args);
  }
};

// ---------------------------------------------------------------------------
// Client-side error reporting — rate-limited & deduplicated
// ---------------------------------------------------------------------------
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const REPORTING_API = BACKEND ? `${BACKEND}/api/client-errors` : "/api/client-errors";

const reported = new Set();
const MAX_REPORTS_PER_MIN = 10;
let recent = [];

function withinBudget() {
  const now = Date.now();
  recent = recent.filter((t) => now - t < 60_000);
  if (recent.length >= MAX_REPORTS_PER_MIN) return false;
  recent.push(now);
  return true;
}

export async function reportClientError({ source, context, message, stack }) {
  if (!message) return;
  // Dedup identical signatures within the session
  const sig = `${source}|${context}|${message}`.slice(0, 400);
  if (reported.has(sig)) return;
  reported.add(sig);
  if (!withinBudget()) return;

  try {
    await axios.post(
      REPORTING_API,
      {
        source: source || "frontend",
        context: context || "",
        message: String(message).slice(0, 1500),
        stack: stack ? String(stack).slice(0, 1500) : "",
        url: typeof window !== "undefined" ? window.location.href : "",
      },
      { timeout: 4000, withCredentials: false },
    );
  } catch (e) {
    // Last-resort log — never throw from the reporter
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn("[mama] reportClientError failed", e?.message);
    }
  }
}

let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (ev) => {
    reportClientError({
      source: "frontend",
      context: "window.error",
      message: ev?.message,
      stack: ev?.error?.stack,
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev?.reason;
    reportClientError({
      source: "frontend",
      context: "unhandledrejection",
      message: typeof reason === "string" ? reason : reason?.message || "unhandled promise",
      stack: reason?.stack,
    });
  });
}
